import { ApolloClient, gql } from '@apollo/client/core';
import {
  Query,
  Mutation,
  SyncSeatingChartMutationInput,
  ArchiveSeatingChartMutationInput,
  DeleteSeatingChartMutationInput,
  DuplicateSeatingChartMutationInput,
  SelectSeatingChartMutationInput,
  RemoveSeatingChartMutationInput,
} from '../../../generated/api.graphql';
import * as SeatingChart from './SeatingChart';
import * as SeatsIoConfig from './SeatsIoConfig';
import { CONNECTION_METADATA_FIELDS, ConnectionArguments } from '../../connection/Api';
import * as GqlConnection from '../../connection/GqlConnection';
import * as VPError from '../../../VPError';

const SEATING_CHART_FIELDS = gql`
  fragment SeatingChartFields on SeatingChart {
    id
    key
    name
    status
    hasDraft
    deletable
    usable
    draftThumbnailUrl
    publishedThumbnailUrl
    parentId
  }
`;

const SEATING_CHARTS_QUERY = gql`
  query SeatingCharts($after: String, $before: String, $first: Int, $last: Int, $includeChildCharts: Boolean) {
    seatingCharts(after: $after, before: $before, first: $first, last: $last, includeChildCharts: $includeChildCharts) {
      edges {
        cursor
        node {
          ...SeatingChartFields
        }
      }
      pageInfo {
        ...ConnectionMetadataFields
      }
    }
  }
  ${SEATING_CHART_FIELDS}
  ${CONNECTION_METADATA_FIELDS}
`;

const SEATING_CHART_QUERY = gql`
  query SeatingChart($id: Int, $key: String, $eventId: Int) {
    seatingChart(id: $id, key: $key, eventId: $eventId) {
      ...SeatingChartFields
    }
  }
  ${SEATING_CHART_FIELDS}
`;

const SEATS_IO_CONFIG_QUERY = gql`
  query SeatsIoConfig {
    seatsIoConfig {
      secretKey
      publicKey
      region
    }
  }
`;

const SYNC_SEATING_CHART_MUTATION = gql`
  mutation SyncSeatingChart($input: SyncSeatingChartMutationInput!) {
    syncSeatingChart(input: $input) {
      seatingChart {
        ...SeatingChartFields
      }
      errors {
        message
        path
      }
    }
  }
  ${SEATING_CHART_FIELDS}
`;

const ARCHIVE_SEATING_CHART_MUTATION = gql`
  mutation ArchiveSeatingChart($input: ArchiveSeatingChartMutationInput!) {
    archiveSeatingChart(input: $input) {
      success
      errors {
        message
        path
      }
    }
  }
`;

const DELETE_SEATING_CHART_MUTATION = gql`
  mutation DeleteSeatingChart($input: DeleteSeatingChartMutationInput!) {
    deleteSeatingChart(input: $input) {
      success
      errors {
        message
        path
      }
    }
  }
`;

const DUPLICATE_SEATING_CHART_MUTATION = gql`
  mutation DuplicateSeatingChart($input: DuplicateSeatingChartMutationInput!) {
    duplicateSeatingChart(input: $input) {
      seatingChart {
        ...SeatingChartFields
      }
      errors {
        message
        path
      }
    }
  }
  ${SEATING_CHART_FIELDS}
`;

const SELECT_SEATING_CHART_MUTATION = gql`
  mutation SelectSeatingChart($input: SelectSeatingChartMutationInput!) {
    selectSeatingChart(input: $input) {
      errors {
        message
        path
      }
    }
  }
`;

const REMOVE_SEATING_CHART_MUTATION = gql`
  mutation RemoveSeatingChart($input: RemoveSeatingChartMutationInput!) {
    removeSeatingChart(input: $input) {
      errors {
        message
        path
      }
    }
  }
`;

// Queries

export const fetchSeatingCharts = async (
  client: ApolloClient<Query>,
  {
    pagination,
    includeChildCharts,
  }: { pagination?: ConnectionArguments; includeChildCharts?: boolean } = {},
): Promise<GqlConnection.GqlConnection<SeatingChart.SeatingChart>> => {
  const { data } = await client.query({
    query: SEATING_CHARTS_QUERY,
    fetchPolicy: 'network-only',
    variables: {
      ...(pagination ?? {}),
      includeChildCharts,
    },
  });

  return GqlConnection.fromGql(data.seatingCharts, SeatingChart.fromGql);
};

export const fetchSeatingChart = async (
  client: ApolloClient<Query>,
  { id, key, eventId }: { id?: number; key?: string; eventId?: number },
): Promise<SeatingChart.SeatingChart | undefined> => {
  const { data } = await client.query({
    query: SEATING_CHART_QUERY,
    fetchPolicy: 'network-only',
    variables: { id, key, eventId },
  });

  return data.seatingChart ? SeatingChart.fromGql(data.seatingChart) : undefined;
};

export const fetchSeatsIoConfig = async (
  client: ApolloClient<Query>,
): Promise<SeatsIoConfig.SeatsIoConfig | undefined> => {
  const { data } = await client.query({
    query: SEATS_IO_CONFIG_QUERY,
    fetchPolicy: 'network-only',
  });

  return data.seatsIoConfig ? SeatsIoConfig.fromGql(data.seatsIoConfig) : undefined;
};

// Mutations

export const syncSeatingChart = async (
  client: ApolloClient<Mutation>,
  input: SyncSeatingChartMutationInput,
): Promise<{ seatingChart?: SeatingChart.SeatingChart; errors: VPError.VPError[] }> => {
  const { data } = await client.mutate({
    mutation: SYNC_SEATING_CHART_MUTATION,
    variables: { input },
  });

  const result = data?.syncSeatingChart;
  return {
    seatingChart: result?.seatingChart ? SeatingChart.fromGql(result.seatingChart) : undefined,
    errors: VPError.fromGqlCollection(result?.errors),
  };
};

export const archiveSeatingChart = async (
  client: ApolloClient<Mutation>,
  input: ArchiveSeatingChartMutationInput,
): Promise<{ success: boolean; errors: VPError.VPError[] }> => {
  const { data } = await client.mutate({
    mutation: ARCHIVE_SEATING_CHART_MUTATION,
    variables: { input },
  });

  const result = data?.archiveSeatingChart;
  return {
    success: result?.success ?? false,
    errors: VPError.fromGqlCollection(result?.errors),
  };
};

export const deleteSeatingChart = async (
  client: ApolloClient<Mutation>,
  input: DeleteSeatingChartMutationInput,
): Promise<{ success: boolean; errors: VPError.VPError[] }> => {
  const { data } = await client.mutate({
    mutation: DELETE_SEATING_CHART_MUTATION,
    variables: { input },
  });

  const result = data?.deleteSeatingChart;
  return {
    success: result?.success ?? false,
    errors: VPError.fromGqlCollection(result?.errors),
  };
};

export const duplicateSeatingChart = async (
  client: ApolloClient<Mutation>,
  input: DuplicateSeatingChartMutationInput,
): Promise<{ seatingChart?: SeatingChart.SeatingChart; errors: VPError.VPError[] }> => {
  const { data } = await client.mutate({
    mutation: DUPLICATE_SEATING_CHART_MUTATION,
    variables: { input },
  });

  const result = data?.duplicateSeatingChart;
  return {
    seatingChart: result?.seatingChart ? SeatingChart.fromGql(result.seatingChart) : undefined,
    errors: VPError.fromGqlCollection(result?.errors),
  };
};

export const selectSeatingChart = async (
  client: ApolloClient<Mutation>,
  input: SelectSeatingChartMutationInput,
): Promise<{ errors: VPError.VPError[] }> => {
  const { data } = await client.mutate({
    mutation: SELECT_SEATING_CHART_MUTATION,
    variables: { input },
  });

  return {
    errors: VPError.fromGqlCollection(data?.selectSeatingChart?.errors),
  };
};

export const removeSeatingChart = async (
  client: ApolloClient<Mutation>,
  input: RemoveSeatingChartMutationInput,
): Promise<{ errors: VPError.VPError[] }> => {
  const { data } = await client.mutate({
    mutation: REMOVE_SEATING_CHART_MUTATION,
    variables: { input },
  });

  return {
    errors: VPError.fromGqlCollection(data?.removeSeatingChart?.errors),
  };
};
